import React from 'react';
import { assetUrl } from '../../utils/api';
import GalleryCategoryCard from './GalleryCategoryCard';
import './GalleryCategoryOverview.css';

function slugifySegment(value, fallback = 'gallery') {
  const source = String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return source || fallback;
}

function resolveImageCandidate(value) {
  if (!value) return '';

  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value !== 'object') return '';

  const candidates = [
    value.optimizedSrc,
    value.optimizedUrl,
    value.originalUrl,
    value.storageUrl,
    value.url,
    value.src,
  ];

  for (const entry of candidates) {
    const candidate = String(entry || '').trim();
    if (candidate) return candidate;
  }

  return '';
}

const CATEGORY_MARKERS = [
  { includes: 'live', marker: '//' },
  { includes: 'behind', marker: '::' },
  { includes: 'portrait', marker: '<>' },
  { includes: 'gear', marker: '++' },
  { includes: 'road', marker: '>>' },
];

export function getGalleryPreviewImage(category, previewByCategory = {}) {
  const firstCategoryImage = Array.isArray(category?.images) ? category.images[0] : null;
  const firstCategoryImageSrc = resolveImageCandidate(firstCategoryImage);
  if (firstCategoryImageSrc) return assetUrl(firstCategoryImageSrc);

  const galleryId = String(category?.id || '').trim();
  const preview = galleryId ? previewByCategory[galleryId] : null;
  const fallbackPreview = String(preview?.thumbs?.[0] || '').trim();
  if (fallbackPreview) return fallbackPreview;

  return '';
}

function resolveCategoryHref(category) {
  const baseValue = category?.id || category?.title || 'gallery';
  const slug = slugifySegment(baseValue, 'gallery');
  return `/gallery/${encodeURIComponent(slug)}`;
}

function resolveCategoryDescription(category) {
  const custom = String(category?.description || category?.beskrivelse || '').trim();
  if (custom) return custom;
  return 'A collection of moments from our world.';
}

function resolveCategoryMarker(category) {
  const source = `${String(category?.title || '').toLowerCase()} ${String(category?.id || '').toLowerCase()}`;
  const found = CATEGORY_MARKERS.find((entry) => source.includes(entry.includes));
  return found ? found.marker : '--';
}

function GalleryCategoryOverview({
  overviewClassName = '',
  overviewStyle,
  categories,
  previewByCategory,
  getCategoryDescription = resolveCategoryDescription,
  onCategoryActivate,
  getCategoryMotion,
  isCategoryDisabled,
  emptyMessage = 'No galleries available right now.',
}) {
  const safeCategories = Array.isArray(categories) ? categories : [];
  const sectionClassName = `dse-gallery-category-overview ${String(overviewClassName || '').trim()}`.trim();

  return (
    <section className={sectionClassName} style={overviewStyle} aria-label="Gallery categories">
      <header className="dse-gallery-category-overview__intro">
        <div className="dse-gallery-category-overview__eyebrow">GALLERY</div>
        <h1 className="dse-gallery-category-overview__title">EXPLORE DØDSEGO</h1>
        <div className="dse-gallery-category-overview__divider" aria-hidden />
        <p className="dse-gallery-category-overview__description">
        </p>
      </header>

      <div className="dse-gallery-category-overview__grid">
        {safeCategories.length ? safeCategories.map((category, index) => {
          const motion = typeof getCategoryMotion === 'function' ? (getCategoryMotion(category, index) || {}) : {};
          const description = getCategoryDescription(category);

          return (
            <GalleryCategoryCard
              key={String(category?.id || `category-${index}`)}
              category={category}
              href={resolveCategoryHref(category)}
              previewImage={getGalleryPreviewImage(category, previewByCategory)}
              description={description}
              marker={resolveCategoryMarker(category)}
              motionStyle={motion.style}
              onActivate={onCategoryActivate}
              disabled={Boolean(motion.disabled) || Boolean(typeof isCategoryDisabled === 'function' ? isCategoryDisabled(category, index) : false)}
            />
          );
        }) : (
          <div className="dse-gallery-category-overview__empty">{emptyMessage}</div>
        )}
      </div>
    </section>
  );
}

export default GalleryCategoryOverview;
